import { communityAxiosInstance, overrideCommunityHeader } from './index';

// Extracts a human-readable message from an Axios error whose response body
// may be JSON ({ message, error }) or an HTML error page (Laravel/nginx style).
export function extractApiError(
  err,
  fallback = 'Something went wrong. Please try again.',
) {
  const data = err?.response?.data;
  if (!data) return fallback;

  // JSON body
  if (typeof data === 'object') {
    return data.message || data.error || fallback;
  }

  // HTML body — pull the visible error text from the page
  if (typeof data === 'string' && data.trim().startsWith('<')) {
    try {
      const doc = new DOMParser().parseFromString(data, 'text/html');
      // Laravel / Symfony error pages put the message in the div after the status code
      const msgEl =
        doc.querySelector('.ml-4') || doc.querySelector('p') || doc.body;
      const text = msgEl?.textContent?.trim();
      return text || fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export const getCommunityProfileRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/me');
};

export const updateCommunityProfileRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.patch('/api/me', data);
};

export const uploadCommunityAvatarRequest = file => {
  overrideCommunityHeader();
  const formData = new FormData();
  formData.append('avatar', file);
  return communityAxiosInstance.post('/api/me/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteCommunityAvatarRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete('/api/me/avatar');
};

export const changeCommunityPasswordRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/password', data);
};

export const initiateEmailChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/email-change');
};

export const resendCurrentEmailChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/email-change/resend-current');
};

export const verifyCurrentEmailChangeRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(
    '/api/me/email-change/verify-current',
    data,
  );
};

export const requestEmailChangeRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/email-change/request', data);
};

export const resendConfirmationEmailChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(
    '/api/me/email-change/resend-confirmation',
  );
};

export const initiateMobileChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/mobile-change');
};

export const resendCurrentMobileChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/mobile-change/resend-current');
};

export const verifyCurrentMobileChangeRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(
    '/api/me/mobile-change/verify-current',
    data,
  );
};

export const requestMobileChangeRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/mobile-change/request', data);
};

export const resendNewMobileChangeRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/mobile-change/resend-new');
};

export const verifyNewMobileChangeRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.post('/api/me/mobile-change/verify-new', data);
};

export const getNotificationPreferencesRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/me/notification-preferences');
};

export const updateNotificationPreferencesRequest = data => {
  overrideCommunityHeader();
  return communityAxiosInstance.patch('/api/me/notification-preferences', data);
};

export const deleteCommunityAccountRequest = (data = {}) => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete('/api/me', { data });
};

export const getGroupsRequest = (params = {}) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/groups', { params });
};

export const getGroupRequest = groupId => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/groups/${groupId}`);
};

export const getGroupPostsRequest = (groupId, page = 1) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/groups/${groupId}/posts`, {
    params: { page },
  });
};

export const likePostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/posts/${postId}/likes`);
};

export const unlikePostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete(`/api/posts/${postId}/likes`);
};

export const joinGroupRequest = groupId => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/groups/${groupId}/membership`);
};

export const leaveGroupRequest = groupId => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete(`/api/groups/${groupId}/membership`);
};

export const getMemberGroupsRequest = (userId, params = {}) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/members/${userId}/groups`, {
    params,
  });
};

export const getGroupMembersRequest = (groupId, params = {}) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/groups/${groupId}/members`, {
    params,
  });
};

export const getMemberRequest = userId => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/members/${userId}`);
};

export const getMemberPostsRequest = (userId, params = {}) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/members/${userId}/posts`, { params });
};

export const getPostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/posts/${postId}`);
};

export const createGroupPostRequest = (groupId, content, mediaIds = []) => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/groups/${groupId}/posts`, {
    content,
    media_ids: mediaIds,
  });
};

export const updateGroupPostRequest = (postId, content, mediaIds = []) => {
  overrideCommunityHeader();
  return communityAxiosInstance.patch(`/api/posts/${postId}`, {
    content,
    media_ids: mediaIds,
  });
};

export const deleteGroupPostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete(`/api/posts/${postId}`);
};

export const getPostActionsRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.get(`/api/posts/${postId}/actions`);
};

export const reportPostRequest = (postId, content) => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/posts/${postId}/reports`, {
    content,
  });
};

export const pinGroupPostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/posts/${postId}/pin`);
};

export const unpinGroupPostRequest = postId => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete(`/api/posts/${postId}/pin`);
};

export const getFaqsRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/faqs');
};

export const getNotificationsRequest = (page = 1, perPage = 10) => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/notifications', {
    params: { page, per_page: perPage },
  });
};

export const markNotificationReadApiRequest = notificationId => {
  overrideCommunityHeader();
  return communityAxiosInstance.patch(
    `/api/notifications/${notificationId}/read`,
  );
};

export const markAllNotificationsReadApiRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.patch('/api/notifications/read-all');
};

export const deleteNotificationApiRequest = notificationId => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete(`/api/notifications/${notificationId}`);
};

export const deleteAllNotificationsApiRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.delete('/api/notifications');
};

export const getCommunityStatsRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/me/stats');
};

export const getCommunityProfileExtendedRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/me/profile');
};

export const joinPlanWaitingListRequest = planId => {
  overrideCommunityHeader();
  return communityAxiosInstance.post(`/api/plans/${planId}/waiting-list`);
};

export const getPlanOfMonthRequest = () => {
  overrideCommunityHeader();
  return communityAxiosInstance.get('/api/plans/plan-of-month');
};
